import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {ConfirmEmailVerification, SendEmailVerification} from './request';
import {MessangerConnectionService} from './messanger-connection.service';
import {StatusMessage} from './response';

@Component({
  selector: 'app-email-verification',
  templateUrl: './email-verification.component.html',
  styleUrls: ['./email-verification.component.css']
})
export class EmailVerificationComponent implements OnInit, OnDestroy {
  @Input() initialEmail = '';
  @Input() showLater = false;
  @Output() verified = new EventEmitter<void>();
  @Output() later = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  email = new FormControl('', [Validators.required, Validators.email, Validators.maxLength(254)]);
  emailCode = new FormControl('', [Validators.required, Validators.pattern('^[0-9]{6}$')]);
  codeRequested = false;
  sending = false;
  confirming = false;
  cooldownSeconds = 0;
  message = '';
  errorMessage = '';
  private cooldownTimer: number | null = null;
  private operationTimer: number | null = null;
  private operationVersion = 0;

  constructor(public con: MessangerConnectionService) {}

  ngOnInit() {
    this.email.setValue(this.initialEmail || '');
    this.initialEmail = '';
  }

  sendCode() {
    if (!this.email.valid || this.sending || this.cooldownSeconds > 0) {
      return;
    }

    const email = (this.email.value || '').trim();
    if (!email) {
      return;
    }

    this.errorMessage = '';
    this.message = '';
    this.sending = true;
    this.startCooldown(60);
    const version = ++this.operationVersion;
    this.startOperationTimeout(version, 'No response was received. You can retry after the cooldown or continue later.');
    this.con.sendRequest(new SendEmailVerification(this.con.appUuid, email), response => {
      if (version !== this.operationVersion) {
        return;
      }
      this.stopOperationTimeout();
      this.sending = false;
      const status = response as StatusMessage;
      if (status.status === 'ok') {
        this.codeRequested = true;
        this.message = 'A verification code was sent to your email.';
      } else {
        this.errorMessage = status.description || 'Could not send the verification email. Please try again later.';
      }
    });
  }

  confirmCode() {
    if (!this.codeRequested || !this.emailCode.valid || this.confirming) {
      return;
    }

    this.errorMessage = '';
    this.message = '';
    this.confirming = true;
    const version = ++this.operationVersion;
    this.startOperationTimeout(version, 'No response was received. Check the code or request another one later.');
    this.con.sendRequest(new ConfirmEmailVerification(this.con.appUuid, this.emailCode.value), response => {
      if (version !== this.operationVersion) {
        return;
      }
      this.stopOperationTimeout();
      this.confirming = false;
      const status = response as StatusMessage;
      if (status.status === 'ok') {
        this.clearTransientState();
        this.verified.emit();
      } else {
        this.errorMessage = status.description || 'The verification code was not accepted. Check it and try again.';
      }
    });
  }

  continueLater() {
    this.clearTransientState();
    this.later.emit();
  }

  cancelFlow() {
    this.clearTransientState();
    this.cancel.emit();
  }

  ngOnDestroy() {
    this.stopCooldown();
    this.clearTransientState();
  }

  private startCooldown(seconds: number) {
    this.stopCooldown();
    this.cooldownSeconds = seconds;
    this.cooldownTimer = window.setInterval(() => {
      this.cooldownSeconds = Math.max(0, this.cooldownSeconds - 1);
      if (this.cooldownSeconds === 0) {
        this.stopCooldown();
      }
    }, 1000);
  }

  private stopCooldown() {
    if (this.cooldownTimer !== null) {
      window.clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    this.cooldownSeconds = 0;
  }

  private clearTransientState() {
    this.operationVersion++;
    this.stopOperationTimeout();
    this.email.setValue('');
    this.emailCode.setValue('');
    this.initialEmail = '';
    this.codeRequested = false;
    this.sending = false;
    this.confirming = false;
    this.message = '';
    this.errorMessage = '';
    this.stopCooldown();
  }

  private startOperationTimeout(version: number, message: string) {
    this.stopOperationTimeout();
    this.operationTimer = window.setTimeout(() => {
      if (version !== this.operationVersion) {
        return;
      }
      this.operationTimer = null;
      this.operationVersion++;
      this.sending = false;
      this.confirming = false;
      this.errorMessage = message;
    }, 15000);
  }

  private stopOperationTimeout() {
    if (this.operationTimer !== null) {
      window.clearTimeout(this.operationTimer);
      this.operationTimer = null;
    }
  }
}
